import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Phone, MapPin } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { trackContactAction } from "@/utils/contactTracking";
import { OptimizedImage } from "@/components/OptimizedImage";
import { getHomeBlurPlaceholder } from "@/utils/homeBlurPlaceholders";

interface HomeGastroProps {
  title?: string;
  overview?: string;
  conclusion?: string;
  primaryButtonText?: string;
  secondaryButtonText?: string;
}

const HomeGastro = ({
  title,
  overview,
  conclusion,
  primaryButtonText,
  secondaryButtonText,
}: HomeGastroProps) => {
  const { t } = useLanguage();

  const defaultTitle = t("features.alpineSanctuary");
  const defaultOverview = t("features.alpineDescription");
  const defaultConclusion = t("features.alpineDescription2");
  const defaultPrimaryButton = t("features.bookYourStay");
  const defaultSecondaryButton = "Explore Alpine Glow Program"; // This might need translation later
  return (
    <>
      <section
        className="relative pt-20 pb-8 px-4 md:px-8 lg:px-16 lg:pt-32 lg:pb-12 bg-white z-20"
        style={{
          isolation: "isolate",
          contain: "layout paint",
          willChange: "auto",
        }}
        data-oid="_ml7_4y"
      >
        <div className="max-w-6xl mx-auto overflow-hidden" data-oid="44v9s1r">
          <motion.h1
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl mb-6 text-gray-900 font-playfair text-center uppercase px-4 leading-tight"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            data-oid="qd2yd0q"
          >
            {title || defaultTitle}
          </motion.h1>

          {/* Two-column layout */}
          <div
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-end mb-12"
            data-oid="41fkco0"
          >
            {/* Left column - Text content */}
            <div className="space-y-6" data-oid="gv429a4">
              <motion.p
                className="text-lg text-gray-900 font-lato leading-relaxed"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                data-oid="uu_9x8g"
              >
                {overview || defaultOverview}
              </motion.p>

              <motion.p
                className="text-lg text-gray-900 font-lato leading-relaxed"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                data-oid="yl4-m1s"
              >
                {conclusion || defaultConclusion}
              </motion.p>
            </div>

            {/* Right column - Gastrosuisse image and text */}
            <motion.div
              className="flex flex-col items-center"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              data-oid="fb.2dj3"
            >
              <div
                className="relative flex items-center justify-center"
                style={{
                  width: "256px",
                  height: "256px",
                  maxWidth: "256px",
                  maxHeight: "256px",
                  flexShrink: 0,
                }}
                data-oid="37nl8d-"
              >
                <OptimizedImage
                  src="/images/gastroswiss_4_stern-256x256.jpg"
                  alt="Gastrosuisse 4-Star Rating"
                  className="w-64 h-64 object-cover object-center"
                  style={{
                    width: "256px",
                    height: "256px",
                    maxWidth: "256px",
                    maxHeight: "256px",
                  }}
                  priority={true}
                  fetchPriority="high"
                  blurDataURL={getHomeBlurPlaceholder("/images/gastroswiss_4_stern-256x256.jpg")}
                  hideLoadingSpinner={true}
                  data-oid="upo.mam"
                />
              </div>

              <motion.div
                className="text-center"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1.0 }}
                data-oid="t-2vy3y"
              >
                <p
                  className="text-xl text-gray-900 font-lato font-semibold uppercase"
                  data-oid="4k79yfs"
                >
                  {t("hero.beatenbergsOnly")}
                </p>
                <p
                  className="text-xl text-alpine-gold font-lato font-semibold uppercase"
                  data-oid="-4jblmn"
                >
                  {t("hero.fourStarGastrosuisse")}
                </p>
                <p
                  className="text-xl text-gray-900 font-lato font-semibold uppercase"
                  data-oid="317mwkx"
                >
                  {t("hero.ratedResort")}
                </p>
              </motion.div>
            </motion.div>
          </div>

          <motion.div
            className="flex flex-col items-center justify-center mt-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.2 }}
            data-oid="action-btn-container"
          >
            <Button
              asChild
              size="lg"
              className="bg-alpine-gold hover:bg-alpine-gold/90 text-white px-8 py-4 text-lg font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 min-w-[250px]"
              data-oid="book-stay-btn"
            >
              <a
                href={import.meta.env.VITE_BOOKING_URL}
              >
                {primaryButtonText || defaultPrimaryButton}
              </a>
            </Button>
            
            <div className="mt-3 flex items-center justify-center gap-2" data-oid="trust-signal">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
              </span>
              <span className="text-sm font-medium text-gray-500/90 tracking-wide uppercase text-[11px]">
                Best Rate Guaranteed
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Information */}
      <section
        className="relative py-8 px-8 md:px-16 bg-white"
        data-oid="1ua.-_w"
      >
        <div className="max-w-6xl mx-auto" data-oid="i7s-4k2">
          <motion.div
            className="flex flex-col sm:flex-row gap-6 items-center justify-center text-gray-900"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.0 }}
            data-oid="zhns0qm"
          >
            <a
              href="tel:+41338410021"
              onClick={() =>
                trackContactAction("concierge", "phone", {
                  phone_number: "+41338410021",
                  contact_source: "homepage_hero_direct",
                  page_location: "homepage_hero_section",
                })
              }
              className="contact-action-track flex items-center gap-2 hover:text-gray-700 transition-colors duration-200"
              data-oid="0mjg-nn"
            >
              <Phone size={18} data-oid="b1b9yfo" />
              <span className="font-lato" data-oid="fok6pra">
                +41 33 841 00 21
              </span>
            </a>

            <a
              href="https://www.google.com/maps/place/Parkhotel+Beatenberg/@46.6976742,7.7919849,17z/data=!3m1!4b1!4m9!3m8!1s0x478fa541a569a32d:0x786359f5bdf1a5e4!5m2!4m1!1i2!8m2!3d46.6976705!4d7.7945598!16s%2Fg%2F1tkc5ln2?entry=ttu&g_ep=EgoyMDI1MDkwOS4wIKXMDSoASAFQAw%3D%3D"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                trackContactAction("concierge", "location", {
                  destination: "parkhotel_beatenberg",
                  contact_source: "homepage_hero_direct",
                  page_location: "homepage_hero_section",
                })
              }
              className="contact-action-track flex items-center gap-2 hover:text-gray-700 transition-colors duration-200"
              data-oid="0al69vf"
            >
              <MapPin size={18} data-oid="3nud:ee" />
              <span className="font-lato" data-oid="qcb6iz4">
                {t("hero.locationButton")}
              </span>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Full-width Revolving Benefits Text */}
      <motion.section
        className="w-full py-6 overflow-hidden relative bg-white"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 1.2 }}
        data-oid="91bj8ut"
      >
        <div className="flex whitespace-nowrap" data-oid="rqhyetb">
          {/* First set of content */}
          <div
            className="flex items-center space-x-12 text-2xl md:text-3xl font-semibold animate-scroll"
            data-oid="rm6owsq"
          >
            <span
              className="text-gray-200"
              style={{
                WebkitTextStroke: "2px #A38648",
                WebkitTextFillColor: "transparent",
              }}
              data-oid="zhwq7sf"
            >
              {t("features.exclusiveRates")}
            </span>
            <span className="text-gray-900 text-4xl" data-oid="y6v4_ka">
              •
            </span>
            <span className="text-gray-900" data-oid="t3mp39j">
              {t("features.guestCardBenefits")}
            </span>
            <span className="text-gray-900 text-4xl" data-oid="wkj1r3-">
              •
            </span>
            <span
              className="text-gray-200"
              style={{
                WebkitTextStroke: "2px #A38648",
                WebkitTextFillColor: "transparent",
              }}
              data-oid="v129d5x"
            >
              {t("features.beatenbergAccess")}
            </span>
            <span className="text-gray-900 text-4xl" data-oid="1mgvhg5">
              •
            </span>
            <span className="text-gray-900" data-oid="e2thaum">
              {t("features.interlakenPrivileges")}
            </span>
            <span className="text-gray-900 text-4xl" data-oid="vlk9kr-">
              •
            </span>
            <span
              className="text-gray-200"
              style={{
                WebkitTextStroke: "2px #A38648",
                WebkitTextFillColor: "transparent",
              }}
              data-oid="62ff0mc"
            >
              {t("features.habkernDiscounts")}
            </span>
            <span className="text-gray-900 text-4xl" data-oid=".d-ix-l">
              •
            </span>
            <span className="text-gray-900" data-oid="mbrxii4">
              {t("features.freeShuttle")}
            </span>
            <span className="text-gray-900 text-4xl" data-oid="lp:86fv">
              •
            </span>
          </div>
          {/* Duplicate set for seamless loop */}
          <div
            className="flex items-center space-x-12 text-2xl md:text-3xl font-semibold animate-scroll ml-12"
            data-oid="1hwcs:4"
          >
            <span
              className="text-gray-200"
              style={{
                WebkitTextStroke: "2px #A38648",
                WebkitTextFillColor: "transparent",
              }}
              data-oid=":3kml0d"
            >
              {t("features.exclusiveRates")}
            </span>
            <span className="text-gray-900 text-4xl" data-oid="b.yw657">
              •
            </span>
            <span className="text-gray-900" data-oid="j6i6mm-">
              {t("features.guestCardBenefits")}
            </span>
            <span className="text-gray-900 text-4xl" data-oid="l6_qqzz">
              •
            </span>
            <span
              className="text-gray-200"
              style={{
                WebkitTextStroke: "2px #A38648",
                WebkitTextFillColor: "transparent",
              }}
              data-oid="earfm.4"
            >
              {t("features.beatenbergAccess")}
            </span>
            <span className="text-gray-900 text-4xl" data-oid="35k5ij2">
              •
            </span>
            <span className="text-gray-900" data-oid=":p7b.5y">
              {t("features.interlakenPrivileges")}
            </span>
            <span className="text-gray-900 text-4xl" data-oid="sog:zi_">
              •
            </span>
            <span
              className="text-gray-200"
              style={{
                WebkitTextStroke: "2px #A38648",
                WebkitTextFillColor: "transparent",
              }}
              data-oid="sgb63.o"
            >
              {t("features.habkernDiscounts")}
            </span>
            <span className="text-gray-900 text-4xl" data-oid="d-uqmdr">
              •
            </span>
            <span className="text-gray-900" data-oid="ryioqz3">
              {t("features.freeShuttle")}
            </span>
            <span className="text-gray-900 text-4xl" data-oid="3b:abd3">
              •
            </span>
          </div>
        </div>

        <style
          dangerouslySetInnerHTML={{
            __html: `
          @keyframes scroll {
            0% { transform: translateX(0); }
            100% { transform: translateX(-100%); }
          }
          .animate-scroll {
            animation: scroll 25s linear infinite;
          }
          @media (max-width: 768px) {
            .animate-scroll {
              animation: scroll 20s linear infinite;
            }
          }
        `,
          }}
          data-oid="j49f:w8"
        />
      </motion.section>
    </>
  );
};

export default HomeGastro;
